import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ComponentProps, useCallback, useEffect, useMemo, useRef } from 'react';

import ItemCard from '@/components/GridItemCard/Card/Card';
import { CarouselItemShelfCard } from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrItemShelfCard/TimedOrItemShelfCard';
import SideScroller from '@/components/SideScroller/SideScroller';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { getRecommendationTypeFromTitle } from '@/utils/amplitude/analytics/item/recommendationTypes';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

const GRID_CHANGE_WIDTH = 550;
const MAX_ITEMS_FOR_MOBILE = 6;
const VISIBILITY_THRESHOLD = 0.5;

type Props = {
    allowBidding?: boolean;
    className?: string;
    hideLiveButton?: boolean;
    identifier: string;
    itemIds: number[];
    onVisibleItemIdsChange?: (visibleItemIds: number[]) => void;
    seeAllUrl?: string;
    showBidStatus?: boolean;
    showSoldBadge?: boolean;
    shouldShowViewAll?: boolean;
    testid?: string;
    title: string;
    trackItemClick?: Function;
} & Partial<ComponentProps<typeof Carousel>>;

export const ItemShelfCarousel = ({
    className = '',
    identifier,
    itemIds = [],
    onVisibleItemIdsChange,
    seeAllUrl = '',
    shouldShowViewAll = false,
    testid,
    title,
    trackItemClick = noop,
}: Props) => {
    const wide = useIsWiderThan(GRID_CHANGE_WIDTH);
    const formatMessage = useAppFormatMessage();
    const recommendationType = getRecommendationTypeFromTitle(title);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onVisibleItemIdsChange) {
            return;
        }

        if (!wide) {
            onVisibleItemIdsChange(itemIds.slice(0, Math.min(itemIds.length, MAX_ITEMS_FOR_MOBILE)));
            return;
        }

        const container = containerRef.current;
        const scrollContainer = container?.querySelector('[role="list"]') as HTMLElement | null;
        if (!scrollContainer) {
            return;
        }

        const listItems = scrollContainer.querySelectorAll(':scope > [role="listitem"]');
        if (listItems.length === 0) {
            return;
        }

        const visibleIndices = new Set<number>();

        const updateVisibleIds = () => {
            const ids = Array.from(visibleIndices)
                .sort((a, b) => a - b)
                .map((i) => itemIds[i])
                .filter((id) => typeof id === 'number');
            onVisibleItemIdsChange(ids);
        };

        const listItemsArray = Array.from(listItems);

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const index = listItemsArray.indexOf(entry.target);
                    if (index === -1) {
                        continue;
                    }
                    if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
                        visibleIndices.add(index);
                    } else {
                        visibleIndices.delete(index);
                    }
                }
                updateVisibleIds();
            },
            { root: scrollContainer, threshold: VISIBILITY_THRESHOLD }
        );

        listItemsArray.forEach((el) => observer.observe(el));

        return () => {
            observer.disconnect();
        };
    }, [wide, itemIds, onVisibleItemIdsChange]);

    const trackClick = useCallback(
        (itemId) => {
            trackItemClick(itemId);
        },
        [trackItemClick]
    );

    const carouselCards = useMemo(
        () =>
            itemIds?.map((itemId, index) => ({
                card: (
                    <ItemCard
                        itemCardOption={CarouselItemShelfCard}
                        itemId={itemId}
                        key={`${index}-${identifier}`}
                        recommendationPosition={index + 1}
                        recommendationType={recommendationType}
                        trackClick={() => trackClick(itemId)}
                    />
                ),
                key: `${index}-${identifier}`,
            })),
        [identifier, itemIds, recommendationType, trackClick]
    );

    if (!itemIds.length) {
        return (
            <div
                className={cn('flex h-[361px] w-full items-center justify-center bg-background-subdued', className)}
                id={identifier}
            >
                <Loading />
            </div>
        );
    }

    const fullCarouselHeader: Pick<ComponentProps<typeof Carousel>, 'title' | 'action'> = {
        ...(seeAllUrl &&
            shouldShowViewAll && {
                action: {
                    path: seeAllUrl,
                    text: String(formatMessage(['viewAll'])),
                },
            }),
        title,
    };

    return (
        <div
            className={cn('flex flex-col bg-background-primary transition-all duration-200 ease-in', className)}
            data-testid={testid}
            id={identifier}
            ref={containerRef}
        >
            <SideScroller
                cards={carouselCards}
                className="w-full"
                {...fullCarouselHeader}
                useOverlayControls
            />
        </div>
    );
};
